import { useForm } from '@inertiajs/react'
import { Send, Check } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'

interface SendToDeveloperCardProps {
  widgetScriptUrl: string
  widgetToken: string | null
  channelId: string
}

export function SendToDeveloperCard({ widgetScriptUrl, widgetToken, channelId }: SendToDeveloperCardProps) {
  const { withAccount } = useRouteParams()
  const [sent, setSent] = useState(false)

  const { data, setData, post, processing, errors, reset } = useForm({
    email: '',
    widget_script_url: widgetScriptUrl,
    widget_token: widgetToken || '',
    channel_id: channelId,
    ...activeBrandParam(),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!data.email) return

    post(routes.settings.sendToDeveloper.path(withAccount()), {
      preserveScroll: true,
      onSuccess: () => {
        setSent(true)
        toast.success('Installation instructions sent!')
        reset('email')
        setTimeout(() => setSent(false), 3000)
      },
      onError: (errors) => {
        if ('authorization' in errors) return
        const errorMessage = errors.email || 'Failed to send email'
        toast.error(errorMessage)
      },
    })
  }

  if (!widgetToken) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="size-5" />
          Send to Developer
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="developer-email">
              Developer Email
            </Label>
            <Input
              id="developer-email"
              type="email"
              placeholder="dev@company.com"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              disabled={processing}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>
          <Button
            type="submit"
            className="cursor-pointer"
            disabled={processing || !data.email}
          >
            {sent ? (
              <>
                <Check className="size-4 mr-1" />
                Sent!
              </>
            ) : processing ? (
              'Sending...'
            ) : (
              <>
                <Send className="size-4 mr-1" />
                Send Instructions
              </>
            )}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground mt-2">
          Sends the embed code with installation instructions.
        </p>
      </CardContent>
    </Card>
  )
}
